import { Component, ComponentRef, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { GoBackButtonService } from '../../services/go-back-button.service';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { OverlayWindowService } from '../single-window/shared/overlay-window.service';
import { StartSalesCreateComponent } from './start-sales-create/start-sales-create.component';
import { StartSalesEditComponent } from './start-sales-edit/start-sales-edit.component';
import { HttpClient } from '../../services/http.service';
import { NotificationService } from '../../services/notification.service';
import { FancyboxService } from '../../shared/fancybox.service';
import { StartSalesMembersComponent } from './start-sales-members/start-sales-members.component';
import { environment } from '../../../environments/environment';


@Component({
    templateUrl: './start-sales.component.html',
    styleUrls: [
        './start-sales.component.scss'
    ],
    standalone: false
})
export class StartSalesComponent implements OnInit, OnDestroy {

  public api_action = 'StartOfSales.get_table';
  public api_reorder_action = 'StartOfSales.reorder';

  public rows: Array<{ [key: string]: any }> = [];
  public count = 0;
  public loading = false;
  public order_saving = false;

  public items_per_page = 50;
  public current_page = 1;
  public pages: number[] = [];
  public global_search_string = '';

  public urlExcel = environment.apiServer + 'restful/start-of-sales-member';

  public createSubscription: Subscription;
  public editSubscription: Subscription;

  /** drag-and-drop */
  public drag_enabled = false;
  public drag_index: number = null;
  private ids_before_drag: string[] = null;

  /** инлайн-редактор даты окончания */
  public editing_end_id: string = null;
  public editing_end_when_publish = false;
  public editing_end_date_control: UntypedFormControl = new UntypedFormControl(null);
  public end_saving = false;

  private _is_archive = false;
  private _on_showcase = true;
  private search$ = new Subject<string>();
  private subscriptions = new Subscription();

  constructor(public http: HttpClient, public route: ActivatedRoute, public router: Router,
              public fb: UntypedFormBuilder, private overlayWindowService: OverlayWindowService,
              private goBack: GoBackButtonService, public notify: NotificationService,
              private lightbox: FancyboxService) {
  }

  ngOnInit() {
    this.goBack.header = '<h1>Старты продаж</h1>';

    this.subscriptions.add(
      this.search$
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe(value => {
          this.global_search_string = value;
          this.current_page = 1;
          this.loadList();
        })
    );

    this.loadList();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (this.createSubscription) {
      this.createSubscription.unsubscribe();
    }
    if (this.editSubscription) {
      this.editSubscription.unsubscribe();
    }
  }

  public get is_archive() {
    return this._is_archive;
  }

  public set is_archive(value: any) {
    this._is_archive = !(value === 'false' || value === false);
    if (this._on_showcase && this._is_archive) {
      this._on_showcase = false;
    }
    this.current_page = 1;
    this.loadList();
  }

  public get on_showcase() {
    return this._on_showcase;
  }

  public set on_showcase(value: any) {
    this._on_showcase = !(value === 'false' || value === false);
    if (this._on_showcase && this._is_archive) {
      this._is_archive = false;
    }
    this.current_page = 1;
    this.loadList();
  }

  /** ---------------------------------------------------------------- данные */

  public prepareQuery(): { [key: string]: any } {
    return {
      'filter': {
        'global_search_string': this.global_search_string || null,
        'fields': {
          'is_archive': this._is_archive,
          'on_showcase': this._on_showcase
        }
      },
      'limit': this.items_per_page,
      'offset': (this.current_page - 1) * this.items_per_page
    };
  }

  public loadList() {
    this.loading = true;
    this.http.post(this.api_action, this.prepareQuery()).subscribe(
      (response: any) => {
        this.rows = (response && response.rows) || [];
        this.count = (response && response.count) || 0;
        this.buildPages();
        this.loading = false;
      },
      (error) => {
        this.loading = false;
        this.notify.notifyError('Ошибка', error, 5000);
      }
    );
  }

  private buildPages() {
    const total = Math.max(1, Math.ceil(this.count / this.items_per_page));
    if (this.current_page > total) {
      this.current_page = total;
    }
    this.pages = Array.from({length: total}, (_, i) => i + 1);
  }

  public onGlobalSearch(value: string) {
    this.search$.next((value || '').trim());
  }

  public changePage(page: number) {
    if (page < 1 || page > this.pages.length || page === this.current_page) {
      return;
    }
    this.current_page = page;
    this.loadList();
  }

  /** ------------------------------------------------------- drag-and-drop */

  public enableDrag() {
    this.drag_enabled = true;
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  public disableDrag() {
    if (this.drag_index === null) {
      this.drag_enabled = false;
    }
  }

  public onDragStart(event: DragEvent, index: number) {
    if (!this.drag_enabled || this._is_archive) {
      event.preventDefault();
      return;
    }
    this.drag_index = index;
    this.ids_before_drag = this.currentIds();
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      // Firefox не начинает перетаскивание без данных
      try {
        event.dataTransfer.setData('text/plain', String(index));
      } catch (e) {
      }
    }
  }

  public onDragOver(event: DragEvent, index: number) {
    if (this.drag_index === null) {
      return;
    }
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
    if (index === this.drag_index) {
      return;
    }
    this.rows.splice(index, 0, ...this.rows.splice(this.drag_index, 1));
    this.drag_index = index;
  }

  public onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.finishDrag();
  }

  public onDragEnd() {
    this.finishDrag();
  }

  private finishDrag() {
    if (this.drag_index === null) {
      return;
    }
    const before = this.ids_before_drag;
    this.drag_index = null;
    this.drag_enabled = false;
    this.ids_before_drag = null;

    const after = this.currentIds();
    if (before && before.join('|') !== after.join('|')) {
      this.saveOrder();
    }
  }

  public moveBy(index: number, delta: number) {
    const target = index + delta;
    if (target < 0 || target >= this.rows.length) {
      return;
    }
    const before = this.currentIds();
    this.rows.splice(target, 0, ...this.rows.splice(index, 1));
    if (before.join('|') !== this.currentIds().join('|')) {
      this.saveOrder();
    }
  }

  /** Перемещение в самый верх/низ ВСЕГО списка — не локально, а прямым запросом,
   *  т.к. затрагивает записи вне текущей страницы/фильтра */
  public moveToTop(row) {
    this.moveToEdge(row, 'top-first');
  }

  public moveToBottom(row) {
    this.moveToEdge(row, 'bottom-last');
  }

  public isFirstOverall(index: number): boolean {
    return index === 0 && this.current_page === 1;
  }

  public isLastOverall(index: number): boolean {
    return index === this.rows.length - 1 && this.current_page === this.pages.length;
  }

  private moveToEdge(row, order_by: 'top-first' | 'bottom-last') {
    this.order_saving = true;
    this.http.post('StartOfSales.order', {
      'start_of_sales_id': row.start_of_sales_id,
      'order_by': order_by
    }).subscribe(
      () => {
        this.order_saving = false;
        this.loadList();
      },
      (error) => {
        this.order_saving = false;
        this.notify.notifyError('Ошибка', error, 5000);
      }
    );
  }

  private currentIds(): string[] {
    return this.rows.map(row => String(row.start_of_sales_id));
  }

  private saveOrder() {
    this.order_saving = true;
    this.http.post(this.api_reorder_action, {'ids': this.currentIds()}).subscribe(
      () => {
        this.order_saving = false;
      },
      (error) => {
        this.order_saving = false;
        this.notify.notifyError('Не удалось сохранить порядок', error, 5000);
        this.loadList();
      }
    );
  }

  /** ---------------------------------------------- редактирование даты окончания */

  public startEditEnd(row) {
    if (row.is_archive) {
      return;
    }
    this.editing_end_id = row.start_of_sales_id;
    this.editing_end_when_publish = !!row.end_when_publish;
    // date_end уже приходит в том же формате, в котором его ожидает
    // app-material-input-datepicker (см. start-sales-edit.component.ts, где
    // это же значение без какой-либо конвертации кладётся в форму редактирования)
    this.editing_end_date_control.setValue(row.end_when_publish ? null : row.date_end);
  }

  public setEditEndMode(when_publish: boolean) {
    this.editing_end_when_publish = when_publish;
  }

  public cancelEditEnd() {
    this.editing_end_id = null;
  }

  public saveEditEnd(row) {
    if (!this.editing_end_when_publish && !this.editing_end_date_control.value) {
      this.notify.notifyError('Ошибка', 'Укажите дату окончания', 3000);
      return;
    }

    const date_end = this.editing_end_when_publish ? null : this.editing_end_date_control.value;
    const data: { [key: string]: any } = {
      'end_when_publish': this.editing_end_when_publish,
      'date_end': date_end
    };

    this.end_saving = true;
    this.http.post('StartOfSales.update', {
      'start_of_sales_id': row.start_of_sales_id,
      'data': data
    }).subscribe(
      () => {
        row.end_when_publish = this.editing_end_when_publish;
        row.date_end = date_end;
        this.end_saving = false;
        this.editing_end_id = null;
        this.notify.notifySuccess('Дата окончания обновлена', '', 1500);
      },
      (error) => {
        this.end_saving = false;
        this.notify.notifyError('Ошибка', error, 5000);
      }
    );
  }

  /** ------------------------------------------------------------ действия */

  createStartSales() {
    this.overlayWindowService.openComponentInPopup(StartSalesCreateComponent)
      .then((factory: ComponentRef<StartSalesCreateComponent>) => {
        this.createSubscription = factory.instance.onCreate.subscribe(_ => this.loadList());
      });
  }

  editStartSales(row) {
    this.overlayWindowService.openComponentInPopup(StartSalesEditComponent, row)
      .then((factory: ComponentRef<StartSalesEditComponent>) => {
        this.editSubscription = factory.instance.onEdit.subscribe(_ => this.loadList());
      });
  }

  participantsStartSales(row) {
    this.overlayWindowService.openComponentInPopup(StartSalesMembersComponent, row)
      .then((factory: ComponentRef<StartSalesMembersComponent>) => {
      });
  }

  deleteStartSales(start_of_sales_id) {
    NotificationService.swalConfirm('Вы уверены?', 'Выбранный старт продаж будет перемещен в архив')
      .then(() => {
        this.http.post('StartOfSales.update',
          {
            'start_of_sales_id': start_of_sales_id,
            'data': {
              'is_archive': true
            }
          }
        ).subscribe(
          (response) => {
            this.notify.notifySuccess('Старт продаж успешно перемещен в архив', '', 2000);
            this.loadList();
          },
          (error) => this.notify.notifyError('Ошибка', error, 5000)
        );
      }).catch(() => false);
  }

}
